import {
  FinanceTransactionKind,
  FinanceTransactionStatus,
  FinanceTransactionType,
  TransactionDirection,
  TransferLegDirection,
  type BudgetLineMatchQuery,
  type CancelFinanceTransactionInput,
  type CreateFinanceTransactionInput,
  type CreateTransferInput,
  type FinanceTransactionListQuery,
  type FinanceTransactionRecord,
  type FinanceTransactionTransferContext,
  type FinanceTransferRecord,
  type UpdateFinanceTransactionMetadataInput,
} from "@/features/finance/model/finance-types"
import { BranchType } from "@ibada/shared-types/common"
import { parseMoney, type BudgetImpactPreview, type BudgetImpactPreviewLine } from "@ibada/shared-types/finance"
import { http } from "@/shared/lib/api/http"
import {
  ZERO_MONEY,
  extractListPayload,
  extractPaginatedListPayload,
  normalizeActivityStatus,
  normalizeBranchSummary,
  normalizeMemberSummary,
  normalizeMoney,
  normalizeNamedEntity,
  normalizeNullableString,
  normalizeString,
  normalizeTransactionAccountSummary,
  type FinanceTransactionListResponse,
} from "./finance-api-shared"


function normalizeTransactionDirection(value: unknown): TransactionDirection {
  if (value === TransactionDirection.Expense) return TransactionDirection.Expense
  if (value === TransactionDirection.TransferOut) return TransactionDirection.TransferOut
  if (value === TransactionDirection.TransferIn) return TransactionDirection.TransferIn
  if (value === "TRANSFER_OUT") return TransactionDirection.TransferOut
  if (value === "TRANSFER_IN") return TransactionDirection.TransferIn
  return TransactionDirection.Income
}


function normalizeTransactionKind(value: unknown): FinanceTransactionKind {
  if (value === FinanceTransactionKind.Reversal) return FinanceTransactionKind.Reversal
  return FinanceTransactionKind.Normal
}


function normalizeTransactionStatus(value: unknown): FinanceTransactionStatus {
  if (value === FinanceTransactionStatus.Voided) return FinanceTransactionStatus.Voided
  if (value === FinanceTransactionStatus.Cancelled) return FinanceTransactionStatus.Cancelled
  return FinanceTransactionStatus.Active
}


function normalizeTransactionLine(value: unknown): FinanceTransactionRecord["lines"][number] | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const accountId = normalizeString(record.accountId ?? record.account_id)
  const account = normalizeTransactionAccountSummary(record.account)

  if (!id || !accountId || !account) return null

  return {
    id,
    accountId,
    amount: normalizeMoney(record.amount) ?? ZERO_MONEY,
    account,
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}


function normalizeTransactionFundAllocation(
  value: unknown,
): FinanceTransactionRecord["fundAllocations"][number] | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const fundId = normalizeString(record.fundId ?? record.fund_id)
  if (!fundId) return null

  return {
    fundId,
    fundName: normalizeString(record.fundName ?? record.fund_name) ?? "",
    fundNameTranslationKey: normalizeNullableString(
      record.fundNameTranslationKey ?? record.fund_name_translation_key,
    ),
    amount: normalizeMoney(record.amount) ?? ZERO_MONEY,
  }
}


function normalizeAccountingAccountSummary(value: unknown): FinanceTransactionRecord["accountingAccount"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const name = normalizeString(record.name)
  if (!id || !name) return null

  const code = normalizeString(record.code)
  // Seeded accounts localise from this key; a CUSTOM account has none and keeps its stored name.
  const translationKey = normalizeString(record.translationKey ?? record.translation_key) ?? null
  return { id, name, translationKey, ...(code ? { code } : {}) }
}


function normalizeTransferContext(input: unknown): FinanceTransactionTransferContext | null {
  if (!input || typeof input !== "object") return null
  const r = input as Record<string, unknown>
  const id = normalizeString(r.id)
  if (!id) return null

  const branch = r.counterpartBranch as Record<string, unknown> | null | undefined
  const branchId = branch ? normalizeString(branch.id) : null

  return {
    id,
    status: normalizeString(r.status) ?? "",
    transferReference: normalizeNullableString(r.transferReference),
    // OUT unless explicitly IN: a transaction whose direction we cannot read is described as leaving, which
    // understates a balance rather than overstating it.
    direction: r.direction === TransferLegDirection.In ? TransferLegDirection.In : TransferLegDirection.Out,
    counterpartBranch:
      branch && branchId
        ? {
            id: branchId,
            name: normalizeString(branch.name) ?? "",
            slug: normalizeString(branch.slug) ?? "",
            branchType: branch.branchType === BranchType.System ? BranchType.System : BranchType.Normal,
          }
        : null,
    createdAt: normalizeString(r.createdAt) ?? "",
    submittedAt: normalizeNullableString(r.submittedAt),
    approvedAt: normalizeNullableString(r.approvedAt),
    rejectedAt: normalizeNullableString(r.rejectedAt),
    cancelledAt: normalizeNullableString(r.cancelledAt),
    createdByUser: normalizeTransactionUserSummary(r.createdByUser),
    submittedByUser: normalizeTransactionUserSummary(r.submittedByUser),
    approvedByUser: normalizeTransactionUserSummary(r.approvedByUser),
    rejectedByUser: normalizeTransactionUserSummary(r.rejectedByUser),
    cancelledByUser: normalizeTransactionUserSummary(r.cancelledByUser),
  }
}

function normalizeTransactionUserSummary(input: unknown): FinanceTransactionRecord["createdByUser"] {
  if (!input || typeof input !== "object") return null
  const r = input as Record<string, unknown>
  const id = normalizeString(r.id)
  if (!id) return null
  return {
    id,
    email: normalizeString(r.email) ?? "",
    firstName: normalizeString(r.firstName ?? r.first_name) ?? "",
    lastName: normalizeString(r.lastName ?? r.last_name) ?? "",
    avatarUrl: normalizeNullableString(r.avatarUrl ?? r.avatar_url),
  }
}


export function normalizeTransactionRecord(input: unknown): FinanceTransactionRecord {
  const record = (input ?? {}) as Record<string, unknown>
  const lines = Array.isArray(record.lines)
    ? record.lines
        .map(normalizeTransactionLine)
        .filter((line): line is NonNullable<typeof line> => line !== null)
    : []
  const normalizedAccount = normalizeTransactionAccountSummary(record.account)
  const singleLineAccount = lines.length === 1 ? lines[0]?.account : null
  const fundAllocations = Array.isArray(record.fundAllocations ?? record.fund_allocations)
    ? ((record.fundAllocations ?? record.fund_allocations) as unknown[])
        .map(normalizeTransactionFundAllocation)
        .filter((allocation): allocation is NonNullable<typeof allocation> => allocation !== null)
    : []

  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    transactionKind: normalizeTransactionKind(record.transactionKind ?? record.transaction_kind),
    accountId:
      normalizeNullableString(record.accountId ?? record.account_id) ??
      (lines.length === 1 ? lines[0]?.accountId ?? null : null),
    transactionReference: normalizeNullableString(record.transactionReference ?? record.transaction_reference),
    type: normalizeTransactionDirection(record.type),
    amount: normalizeMoney(record.amount) ?? ZERO_MONEY,
    currency: (normalizeString(record.currency) as FinanceTransactionRecord["currency"]) ?? null,
    accountingAccountId: normalizeNullableString(record.accountingAccountId ?? record.accounting_account_id),
    activityId: normalizeNullableString(record.activityId ?? record.activity_id),
    programId: normalizeNullableString(record.programId ?? record.program_id),
    pledgeId: normalizeNullableString(record.pledgeId ?? record.pledge_id),
    memberId: normalizeNullableString(record.memberId ?? record.member_id),
    groupId: normalizeNullableString(record.groupId ?? record.group_id),
    ministryId: normalizeNullableString(record.ministryId ?? record.ministry_id),
    paymentMethod: (normalizeString(record.paymentMethod ?? record.payment_method) as FinanceTransactionRecord["paymentMethod"]) ?? null,
    paymentReference: normalizeNullableString(record.paymentReference ?? record.payment_reference),
    description: normalizeNullableString(record.description),
    occurredAt: normalizeString(record.occurredAt ?? record.occurred_at) ?? new Date().toISOString(),
    status: normalizeTransactionStatus(record.status),
    voidedAt: normalizeNullableString(record.voidedAt ?? record.voided_at),
    cancelledAt: normalizeNullableString(record.cancelledAt ?? record.cancelled_at),
    cancelledByUserId: normalizeNullableString(record.cancelledByUserId ?? record.cancelled_by_user_id),
    cancelledBy: normalizeMemberSummary(record.cancelledBy ?? record.cancelled_by),
    cancelledByTransactionId: normalizeNullableString(
      record.cancelledByTransactionId ?? record.cancelled_by_transaction_id,
    ),
    cancellationReason: normalizeNullableString(record.cancellationReason ?? record.cancellation_reason),
    /*
     * The two halves of a reversal pair.
     *
     * The list endpoint states these as `cancelledByTransactionId` (the reversal that undid this row) and
     * `reversesTransactionId` (the row this one undoes) — the column names, not the names read here. Without
     * the fallbacks both fields were null on every listed transaction, so the detail sheet's "reverses"
     * explanation and its reversal rows could never render on a record loaded from the list.
     */
    reversalTransactionId: normalizeNullableString(
      record.reversalTransactionId ??
        record.reversal_transaction_id ??
        record.cancelledByTransactionId ??
        record.cancelled_by_transaction_id,
    ),
    reversedTransactionId: normalizeNullableString(
      record.reversedTransactionId ??
        record.reversed_transaction_id ??
        record.reversesTransactionId ??
        record.reverses_transaction_id,
    ),
    reversedTransactionReference: normalizeNullableString(
      record.reversedTransactionReference ??
        record.reversesTransactionReference ??
        record.reverses_transaction_reference,
    ),
    reversalTransactionReference: normalizeNullableString(
      record.reversalTransactionReference ?? record.reversal_transaction_reference,
    ),
    accountingAccount: normalizeAccountingAccountSummary(record.accountingAccount ?? record.accounting_account),
    activity: normalizeActivitySummary(record.activity),
    branch: normalizeBranchSummary(record.branch),
    account: normalizedAccount ?? singleLineAccount,
    lines,
    fundAllocations,
    member: normalizeMemberSummary(record.member),
    group: normalizeNamedEntity(record.group),
    ministry: normalizeNamedEntity(record.ministry),
    departmentSource: normalizeNamedEntity(record.departmentSource ?? record.department_source),
    pledge: normalizePledgeSummary(record.pledge),
    createdByUser: normalizeTransactionUserSummary(record.createdByUser ?? record.created_by_user),
    transfer: normalizeTransferContext(record.transfer),
    isInApprovedReconciliation: Boolean(record.isInApprovedReconciliation),
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}

function normalizePledgeSummary(value: unknown): FinanceTransactionRecord["pledge"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  if (!id) return null

  const rawStatus = normalizeString(record.status)
  const status =
    rawStatus === "PARTIALLY_FULFILLED" ||
    rawStatus === "FULFILLED" ||
    rawStatus === "CANCELLED" ||
    rawStatus === "OVERDUE"
      ? rawStatus
      : "OPEN"

  return {
    id,
    status,
    amountPledged: normalizeMoney(record.amountPledged ?? record.amount_pledged) ?? ZERO_MONEY,
    amountReceived: normalizeMoney(record.amountReceived ?? record.amount_received) ?? ZERO_MONEY,
  }
}


function normalizeActivitySummary(value: unknown): FinanceTransactionRecord["activity"] {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const id = normalizeString(record.id)
  const title = normalizeString(record.title)
  const startsAt = normalizeString(record.startsAt ?? record.starts_at)
  const endsAt = normalizeString(record.endsAt ?? record.ends_at)

  if (!id || !title || !startsAt || !endsAt) return null

  return {
    id,
    // The bridge to the operational Activity (ADR-0017). Absent on older payloads and null for a legacy
    // standalone finance activity — either way there is no /activities page, and callers render no link.
    activityId: normalizeNullableString(record.activityId ?? record.activity_id),
    title,
    status: normalizeActivityStatus(record.status),
    branchId: normalizeNullableString(record.branchId ?? record.branch_id),
    incomeCategoryId: normalizeNullableString(record.incomeCategoryId ?? record.income_category_id),
    fundId: normalizeNullableString(record.fundId ?? record.fund_id),
    startsAt,
    endsAt,
  }
}


function buildTransactionsParams(query: FinanceTransactionListQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    workspaceId: query.workspaceId,
    includeReversals: query.includeReversals === true ? "true" : "false",
    branchId: query.branchId,
  }

  if (query.departmentId) params.departmentId = query.departmentId
  // categories→accounts: the list endpoint filters by the income/expense AccountingAccount id.
  if (query.accountingAccountId) params.accountingAccountId = query.accountingAccountId
  if (query.activityId) params.activityId = query.activityId
  if (query.fundId) params.fundId = query.fundId
  if (query.pledgeId) params.pledgeId = query.pledgeId
  if (query.groupId) params.groupId = query.groupId
  if (query.ministryId) params.ministryId = query.ministryId
  if (query.memberId) params.memberId = query.memberId
  if (query.status) params.status = query.status
  if (query.type) params.type = query.type
  if (query.dateFrom) params.dateFrom = query.dateFrom
  if (query.dateTo) params.dateTo = query.dateTo
  if (query.search) params.search = query.search
  if (typeof query.page === "number" && query.page > 0) params.page = query.page
  if (typeof query.limit === "number" && query.limit > 0) params.limit = Math.min(query.limit, 100)
  if (query.sortBy) params.sortBy = query.sortBy
  if (query.sortOrder) params.sortOrder = query.sortOrder

  return params
}


export async function listFinanceTransactionRecords(query: FinanceTransactionListQuery): Promise<FinanceTransactionRecord[]> {
  const response = await http.get<unknown>("/finance/transactions", {
    params: buildTransactionsParams(query),
  })

  return extractListPayload(response.data, ["items", "transactions"], normalizeTransactionRecord)
}


export async function listFinanceTransactionRecordPage(
  query: FinanceTransactionListQuery,
): Promise<FinanceTransactionListResponse> {
  const response = await http.get<unknown>("/finance/transactions", {
    params: buildTransactionsParams(query),
  })

  return extractPaginatedListPayload(response.data, ["items", "transactions"], normalizeTransactionRecord)
}

/** Query for `GET /finance/departments/summary` — the per-department rollup for a date range. */
export type ActivityProgramIncomeBreakdownItem = {
  programId: string
  title: string
  /** Money: a canonical decimal string at the branch currency's scale (IBA-116). Display it; never sum it. */
  income: string
  transactionCount: number
}

export async function fetchActivityProgramIncomeBreakdown(params: {
  workspaceId: string
  activityId: string
  branchId?: string | null
}): Promise<ActivityProgramIncomeBreakdownItem[]> {
  const response = await http.get<{ items: ActivityProgramIncomeBreakdownItem[] }>(
    "/finance/transactions/program-breakdown",
    { params },
  )
  return response.data?.items ?? []
}


export async function createTransactionRecord(input: CreateFinanceTransactionInput): Promise<FinanceTransactionRecord> {
  const response = await http.post<unknown>("/finance/transactions", {
    workspaceId: input.workspaceId,
    branchId: input.branchId,
    ...(input.departmentId ? { departmentId: input.departmentId } : {}),
    ...(typeof input.accountId !== "undefined" ? { accountId: input.accountId } : {}),
    // IBA-154 (D9): the fund an EXPENSE draws down. Never sent for income — income is designated by its
    // category's linked fund, and sending one is rejected rather than ignored.
    ...(typeof input.fundId !== "undefined" && input.fundId !== null ? { fundId: input.fundId } : {}),
    ...(input.budgetImpactRevision ? { budgetImpactRevision: input.budgetImpactRevision } : {}),
    // IBA-67 opt-in fund split (income only). Omitted ⇒ 100% routes to the income account's linked fund; present
    // ⇒ the server re-validates the lines sum to `amount` and name distinct funds.
    ...(Array.isArray(input.fundAllocations) && input.fundAllocations.length > 0
      ? { fundAllocations: input.fundAllocations.map((line) => ({ fundId: line.fundId, amount: line.amount })) }
      : {}),
    type: input.type,
    amount: input.amount,
    // categories→accounts: the authoritative income/expense account. The legacy category ids are ONLY sent when
    // a pledge/activity flow locks one (the transitional bridge) — the normal account-based form sends neither,
    // so no `incomeCategoryId: null` / `expenseCategoryId: null` goes on the wire.
    accountingAccountId: input.accountingAccountId ?? null,
    ...(input.incomeCategoryId ? { incomeCategoryId: input.incomeCategoryId } : {}),
    ...(input.expenseCategoryId ? { expenseCategoryId: input.expenseCategoryId } : {}),
    activityId: input.activityId ?? null,
    programId: input.programId ?? null,
    pledgeId: input.pledgeId ?? null,
    memberId: input.memberId ?? null,
    groupId: input.groupId ?? null,
    departmentSourceId: input.departmentSourceId ?? null,
    paymentMethod: input.paymentMethod ?? null,
    paymentReference: input.paymentReference ?? null,
    description: input.description ?? null,
    occurredAt: input.occurredAt,
  })

  return normalizeTransactionRecord(response.data)
}


function normalizeTransferRecord(input: unknown): FinanceTransferRecord {
  const record = (input ?? {}) as Record<string, unknown>

  return {
    id: normalizeString(record.id) ?? "",
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    // This endpoint only ever returns TRANSFER-type records.
    type: FinanceTransactionType.Transfer,
    transactionReference: normalizeNullableString(record.transactionReference ?? record.transaction_reference),
    amount: normalizeMoney(record.amount) ?? ZERO_MONEY,
    currency: (normalizeString(record.currency) as FinanceTransferRecord["currency"]) ?? null,
    sourceAccountId: normalizeString(record.sourceAccountId ?? record.source_account_id) ?? "",
    destinationAccountId: normalizeString(record.destinationAccountId ?? record.destination_account_id) ?? "",
    sourceAccount: normalizeTransactionAccountSummary(record.sourceAccount ?? record.source_account),
    destinationAccount: normalizeTransactionAccountSummary(record.destinationAccount ?? record.destination_account),
    reference: normalizeNullableString(record.reference),
    description: normalizeNullableString(record.description),
    occurredAt: normalizeString(record.occurredAt ?? record.occurred_at) ?? new Date().toISOString(),
    status: normalizeTransactionStatus(record.status),
    transactionKind: normalizeTransactionKind(record.transactionKind ?? record.transaction_kind),
    journalEntryId: normalizeNullableString(record.journalEntryId ?? record.journal_entry_id),
    createdAt: normalizeString(record.createdAt ?? record.created_at) ?? new Date().toISOString(),
    updatedAt: normalizeString(record.updatedAt ?? record.updated_at) ?? new Date().toISOString(),
  }
}


/** Create an internal account-to-account transfer (POST /finance/transfers). */
export async function createTransfer(input: CreateTransferInput): Promise<FinanceTransferRecord> {
  const response = await http.post<unknown>("/finance/transfers", {
    workspaceId: input.workspaceId,
    sourceAccountId: input.sourceAccountId,
    destinationAccountId: input.destinationAccountId,
    amount: input.amount,
    occurredAt: input.occurredAt,
    ...(input.reference ? { reference: input.reference } : {}),
    ...(input.description ? { description: input.description } : {}),
  })

  return normalizeTransferRecord(response.data)
}


function normalizeBudgetImpactPreviewLine(value: unknown): BudgetImpactPreviewLine | null {
  if (!value || typeof value !== "object") return null
  const record = value as Record<string, unknown>
  const budgetLineId = normalizeString(record.budgetLineId)
  const budgetId = normalizeString(record.budgetId)
  const budgetTitle = normalizeString(record.budgetTitle)
  const budgetLineTitle = normalizeString(record.budgetLineTitle)
  const fundId = normalizeString(record.fundId)
  const fundName = normalizeString(record.fundName)
  const currency = normalizeString(record.currency)
  const amount = normalizeMoney(record.amount)
  if (!budgetLineId || !budgetId || !budgetTitle || !budgetLineTitle || !fundId || !fundName || !currency || !amount) {
    return null
  }

  let canonicalAmount: BudgetImpactPreviewLine["amount"]
  try {
    canonicalAmount = parseMoney(amount, currency)
  } catch {
    return null
  }

  return {
    budgetId,
    budgetTitle,
    budgetLineId,
    budgetLineTitle,
    fundId,
    fundName,
    amount: canonicalAmount,
    currency,
  }
}


/** IBA-69: preview which budget lines a not-yet-created transaction would match. */
export async function fetchBudgetLineMatches(query: BudgetLineMatchQuery): Promise<BudgetImpactPreview> {
  const response = await http.post<unknown>("/finance/transactions/budget-line-matches", query)

  const record = (response.data ?? {}) as Record<string, unknown>
  const revision = normalizeString(record.revision)
  if (!revision || !/^sha256:[a-f0-9]{64}$/.test(revision) || !Array.isArray(record.impacts)) {
    throw new Error("Invalid budget impact preview response")
  }
  const impacts: BudgetImpactPreviewLine[] = []
  for (const rawImpact of record.impacts) {
    const impact = normalizeBudgetImpactPreviewLine(rawImpact)
    if (!impact) throw new Error("Invalid budget impact preview response")
    impacts.push(impact)
  }

  return { revision, impacts }
}


/**
 * Edit a posted transaction's METADATA — description, payment method, payment reference.
 *
 * Nothing financial: amount, accounts, fund, date and branch are immutable on a posted transaction and the API
 * answers 409 for any of them. Correcting a real figure is still a cancel + reversal, because the ledger is a
 * record of what happened, not of what we currently believe.
 */
export async function updateFinanceTransactionMetadata(
  input: UpdateFinanceTransactionMetadataInput,
): Promise<FinanceTransactionRecord> {
  const { id, ...body } = input
  const response = await http.patch<unknown>(`/finance/transactions/${id}`, body)
  return normalizeTransactionRecord(response.data)
}

export async function cancelFinanceTransaction(input: CancelFinanceTransactionInput): Promise<FinanceTransactionRecord> {
  const response = await http.post<unknown>(`/finance/transactions/${input.id}/cancel`, {
    confirmationPhrase: input.confirmationPhrase,
    cancellationReason: input.cancellationReason,
  })
  return normalizeTransactionRecord(response.data)
}
